import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Mail, User, Building, Briefcase, Target,
  CheckCircle, AlertCircle, Loader2, Sparkles, Shield, Zap, Clock
} from 'lucide-react';
import ParticleBackground from '../components/ParticleBackground';
import AnimatedBackground from '../components/AnimatedBackground';
import GlowingCard from '../components/GlowingCard';
import { joinWaitlist, submitDemoRequest } from '../lib/api';
import { useForm } from '../hooks/useForm';

export default function SignupPage() {
  const [signupType, setSignupType] = useState('waitlist'); // 'waitlist' or 'demo'

  const waitlistForm = useForm(
    { email: '', name: '', company: '', role: '', interest: '' },
    joinWaitlist
  );

  const demoForm = useForm(
    {
      firstName: '',
      lastName: '',
      email: '',
      company: '',
      jobTitle: '',
      companySize: '',
      industry: '',
      useCase: '',
      message: '',
    },
    submitDemoRequest
  );

  const form = signupType === 'waitlist' ? waitlistForm : demoForm;

  const waitlistValidation = {
    email: { required: true, email: true },
  };

  const demoValidation = {
    firstName: { required: true, message: 'First name is required' },
    lastName: { required: true, message: 'Last name is required' },
    email: { required: true, email: true },
    company: { required: true, message: 'Company name is required' },
  };

  const companySizes = [
    '1-10',
    '11-50',
    '51-200',
    '201-500',
    '501-1000',
    '1000+',
  ];

  const industries = [
    'Technology',
    'Financial Services',
    'Healthcare',
    'Manufacturing',
    'Retail',
    'Government',
    'Education',
    'Other',
  ];

  const interests = [
    'AI Governance',
    'Autonomous Agents',
    'Enterprise Security',
    'Compliance & Audit',
    'Just Exploring',
  ];

  if (form.submitSuccess) {
    return (
      <div className="relative min-h-screen pt-20 flex items-center justify-center">
        <AnimatedBackground variant="gaming" />
        <ParticleBackground density={20} />

        <div className="max-w-lg mx-auto px-6 py-24 text-center">
          <GlowingCard glowColor="green">
            <div className="p-12">
              <div className="w-20 h-20 rounded-full bg-green-500/20 border border-green-500/30 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-4">
                {signupType === 'waitlist' ? "You're on the List!" : 'Demo Request Received!'}
              </h1>
              <p className="text-slate-400 mb-8">
                {signupType === 'waitlist'
                  ? "We'll notify you when we're ready to onboard new users. Stay tuned!"
                  : 'Our team will reach out within 24 hours to schedule your personalized demo.'}
              </p>
              <div className="flex gap-4 justify-center">
                <button
                  onClick={form.reset}
                  className="px-6 py-3 rounded-xl font-semibold border border-slate-600 text-white hover:border-blue-500/50 transition-all"
                >
                  Submit Another
                </button>
                <Link
                  to="/"
                  className="px-6 py-3 rounded-xl font-semibold bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white transition-all"
                >
                  Back to Home
                </Link>
              </div>
            </div>
          </GlowingCard>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen pt-20">
      <AnimatedBackground variant="gaming" />
      <ParticleBackground density={15} />

      <div className="max-w-6xl mx-auto px-6 py-16">
        {/* Back Link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Form Section */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 mb-6">
              <Sparkles className="w-4 h-4 text-purple-400" />
              <span className="text-sm text-purple-400 font-medium">Early Access</span>
            </div>

            <h1 className="text-4xl font-bold text-white mb-4">
              Get Started with CAO Platform
            </h1>
            <p className="text-lg text-slate-400 mb-8">
              Join the future of AI governance. Choose your path below.
            </p>

            {/* Toggle */}
            <div className="flex gap-2 mb-8">
              <button
                onClick={() => setSignupType('waitlist')}
                className={`flex-1 py-3 rounded-xl font-medium transition-all ${
                  signupType === 'waitlist'
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                    : 'bg-slate-800/50 border border-slate-700 text-slate-400 hover:text-white'
                }`}
              >
                Join Waitlist
              </button>
              <button
                onClick={() => setSignupType('demo')}
                className={`flex-1 py-3 rounded-xl font-medium transition-all ${
                  signupType === 'demo'
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                    : 'bg-slate-800/50 border border-slate-700 text-slate-400 hover:text-white'
                }`}
              >
                Request Demo
              </button>
            </div>

            <GlowingCard glowColor={signupType === 'waitlist' ? 'blue' : 'purple'}>
              <form
                onSubmit={(e) =>
                  form.handleSubmit(
                    e,
                    signupType === 'waitlist' ? waitlistValidation : demoValidation
                  )
                }
                className="p-8 space-y-6"
              >
                {form.submitError && (
                  <div className="flex items-center gap-2 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400">
                    <AlertCircle className="w-5 h-5 shrink-0" />
                    <p>{form.submitError}</p>
                  </div>
                )}

                {signupType === 'waitlist' ? (
                  <>
                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Email Address *
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                        <input
                          type="email"
                          name="email"
                          value={form.values.email}
                          onChange={form.handleChange}
                          placeholder="you@company.com"
                          className={`w-full pl-10 pr-4 py-3 rounded-xl bg-slate-800/50 border ${
                            form.errors.email ? 'border-red-500/50' : 'border-slate-700'
                          } text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50 transition-colors`}
                        />
                      </div>
                      {form.errors.email && (
                        <p className="text-red-400 text-sm mt-1">{form.errors.email}</p>
                      )}
                    </div>

                    {/* Name (Optional) */}
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Full Name (Optional)
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                        <input
                          type="text"
                          name="name"
                          value={form.values.name}
                          onChange={form.handleChange}
                          placeholder="John Doe"
                          className="w-full pl-10 pr-4 py-3 rounded-xl bg-slate-800/50 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50 transition-colors"
                        />
                      </div>
                    </div>

                    {/* Company (Optional) */}
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Company (Optional)
                      </label>
                      <div className="relative">
                        <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                        <input
                          type="text"
                          name="company"
                          value={form.values.company}
                          onChange={form.handleChange}
                          placeholder="Acme Inc."
                          className="w-full pl-10 pr-4 py-3 rounded-xl bg-slate-800/50 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50 transition-colors"
                        />
                      </div>
                    </div>

                    {/* Interest */}
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Primary Interest
                      </label>
                      <select
                        name="interest"
                        value={form.values.interest}
                        onChange={form.handleChange}
                        className="w-full px-4 py-3 rounded-xl bg-slate-800/50 border border-slate-700 text-white focus:outline-none focus:border-blue-500/50 transition-colors"
                      >
                        <option value="">Select your interest...</option>
                        {interests.map((interest) => (
                          <option key={interest} value={interest}>
                            {interest}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Name Fields */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          First Name *
                        </label>
                        <input
                          type="text"
                          name="firstName"
                          value={form.values.firstName}
                          onChange={form.handleChange}
                          placeholder="John"
                          className={`w-full px-4 py-3 rounded-xl bg-slate-800/50 border ${
                            form.errors.firstName ? 'border-red-500/50' : 'border-slate-700'
                          } text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50 transition-colors`}
                        />
                        {form.errors.firstName && (
                          <p className="text-red-400 text-sm mt-1">{form.errors.firstName}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Last Name *
                        </label>
                        <input
                          type="text"
                          name="lastName"
                          value={form.values.lastName}
                          onChange={form.handleChange}
                          placeholder="Doe"
                          className={`w-full px-4 py-3 rounded-xl bg-slate-800/50 border ${
                            form.errors.lastName ? 'border-red-500/50' : 'border-slate-700'
                          } text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50 transition-colors`}
                        />
                        {form.errors.lastName && (
                          <p className="text-red-400 text-sm mt-1">{form.errors.lastName}</p>
                        )}
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Work Email *
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                        <input
                          type="email"
                          name="email"
                          value={form.values.email}
                          onChange={form.handleChange}
                          placeholder="john@company.com"
                          className={`w-full pl-10 pr-4 py-3 rounded-xl bg-slate-800/50 border ${
                            form.errors.email ? 'border-red-500/50' : 'border-slate-700'
                          } text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50 transition-colors`}
                        />
                      </div>
                      {form.errors.email && (
                        <p className="text-red-400 text-sm mt-1">{form.errors.email}</p>
                      )}
                    </div>

                    {/* Company & Job Title */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Company *
                        </label>
                        <div className="relative">
                          <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                          <input
                            type="text"
                            name="company"
                            value={form.values.company}
                            onChange={form.handleChange}
                            placeholder="Acme Inc."
                            className={`w-full pl-10 pr-4 py-3 rounded-xl bg-slate-800/50 border ${
                              form.errors.company ? 'border-red-500/50' : 'border-slate-700'
                            } text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50 transition-colors`}
                          />
                        </div>
                        {form.errors.company && (
                          <p className="text-red-400 text-sm mt-1">{form.errors.company}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Job Title
                        </label>
                        <div className="relative">
                          <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                          <input
                            type="text"
                            name="jobTitle"
                            value={form.values.jobTitle}
                            onChange={form.handleChange}
                            placeholder="VP of Engineering"
                            className="w-full pl-10 pr-4 py-3 rounded-xl bg-slate-800/50 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50 transition-colors"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Company Size & Industry */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Company Size
                        </label>
                        <select
                          name="companySize"
                          value={form.values.companySize}
                          onChange={form.handleChange}
                          className="w-full px-4 py-3 rounded-xl bg-slate-800/50 border border-slate-700 text-white focus:outline-none focus:border-blue-500/50 transition-colors"
                        >
                          <option value="">Select...</option>
                          {companySizes.map((size) => (
                            <option key={size} value={size}>
                              {size} employees
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Industry
                        </label>
                        <select
                          name="industry"
                          value={form.values.industry}
                          onChange={form.handleChange}
                          className="w-full px-4 py-3 rounded-xl bg-slate-800/50 border border-slate-700 text-white focus:outline-none focus:border-blue-500/50 transition-colors"
                        >
                          <option value="">Select...</option>
                          {industries.map((industry) => (
                            <option key={industry} value={industry}>
                              {industry}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Use Case */}
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Primary Use Case
                      </label>
                      <div className="relative">
                        <Target className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                        <input
                          type="text"
                          name="useCase"
                          value={form.values.useCase}
                          onChange={form.handleChange}
                          placeholder="e.g., Customer service automation"
                          className="w-full pl-10 pr-4 py-3 rounded-xl bg-slate-800/50 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50 transition-colors"
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={form.isSubmitting}
                  className={`w-full py-4 rounded-xl font-semibold text-white transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                    signupType === 'waitlist'
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500'
                      : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500'
                  }`}
                >
                  {form.isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {signupType === 'waitlist' ? 'Joining...' : 'Submitting...'}
                    </>
                  ) : signupType === 'waitlist' ? (
                    'Join Waitlist'
                  ) : (
                    'Request Demo'
                  )}
                </button>
              </form>
            </GlowingCard>
          </div>

          {/* Benefits */}
          <div className="space-y-8">
            <GlowingCard glowColor="cyan">
              <div className="p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Why CAO Platform?</h2>
                <div className="space-y-6">
                  {[
                    {
                      icon: Shield,
                      title: 'Enterprise-Grade Security',
                      desc: 'SOC 2 Type II certified with end-to-end encryption',
                    },
                    {
                      icon: Zap,
                      title: 'Real-Time Governance',
                      desc: 'Millisecond decision latency for autonomous agents',
                    },
                    {
                      icon: Clock,
                      title: 'Complete Audit Trail',
                      desc: 'Immutable logs for every AI decision and action',
                    },
                    {
                      icon: Sparkles,
                      title: 'AI Executive Council',
                      desc: 'Specialized AI agents for every business function',
                    },
                  ].map((feature, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center shrink-0">
                        <feature.icon className="w-6 h-6 text-cyan-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-white">{feature.title}</h3>
                        <p className="text-sm text-slate-400">{feature.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </GlowingCard>

            <GlowingCard glowColor="gold">
              <div className="p-8">
                <h3 className="text-lg font-semibold text-white mb-4">Trusted By</h3>
                <p className="text-slate-400 text-sm mb-4">
                  Leading enterprises building the future of autonomous AI
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {['Fortune 500', 'Tech Leaders', 'Healthcare', 'Finance'].map((category) => (
                    <div
                      key={category}
                      className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/50 text-center"
                    >
                      <p className="text-sm text-slate-300">{category}</p>
                    </div>
                  ))}
                </div>
              </div>
            </GlowingCard>
          </div>
        </div>
      </div>
    </div>
  );
}
